// 'rectangle' is used for debugging map bounds issues.
import {
    control,
    Control,
    divIcon,
    DivIcon,
    DomEvent,
    DomUtil,
    featureGroup,
    FeatureGroup,
    icon,
    Icon,
    latLng,
    LatLng,
    latLngBounds,
    Map as LeafletMap,
    map,
    marker as LeafletMarker,
    Marker,
    markerClusterGroup,
    MarkerClusterGroup,
    polyline,
    Polyline,
    PopupEvent,
    PopupOptions,
    tileLayer,
} from 'leaflet';
import 'leaflet.markercluster';
import has from 'lodash-es/has';
import pick from 'lodash-es/pick';
import { debounceTime, filter, first, take, takeUntil, timeout } from 'rxjs/operators';
import { fromEvent } from 'rxjs/internal/observable/fromEvent';
import { merge } from 'rxjs/internal/observable/merge';
import { Observable, of, Subject } from 'rxjs';

import {
    FINDER_MAP_BASE,
    MARKER_TYPES,
    MARKER_CLUSTER_OPTIONS
} from '@finder/shared/components/map/defaults/map.defaults.constants';
import { ConfigService } from '@finder/core/config.service';
import { MapInterface, MapDataLocationMarker, MapButtonOptions, MarkerOptions, FinderMakerLeafletMarker } from './map.interface';
import { KEYBOARD, MOUSE } from '@finder/shared/constants/user-input.constants';
import { MapCardService } from '@finder/shared/services/map-card/map-card.service';
import { FinderAnalyticsService } from '@finder/shared/services/finder-analytics/finder-analytics.service';
import { LEAFLET_CONSTANTS } from './leaflet.constants';
import { DebugLogService } from '@finder/shared/services/debug-log/debug-log.service';
import { WindowRef } from '@finder/shared/services/window-ref/window-ref.service';
import { timeout as rxjsTimeout } from '@finder/shared/utils/time/timeout';
import { OperatingStatusService } from '@finder/shared/components/operating-status/operating-status.service';
import { OperatingStatus } from '../../operating-status/operating-status.interface';

// Capture the object from the window object
declare const L: any;

const CLUSTER_LEVEL = 18;

export class Leaflet implements MapInterface {
    // Made public for testability
    map: LeafletMap;
    markers: Map<string, Marker> = new Map(); // Markers stored with lat,lng-urlFriendlyId as key
    iconHover: Map<string, any> = new Map(); // Markers stored with lat,lng-urlFriendlyId as key
    markersByFacilityId: Map<string, FinderMakerLeafletMarker> = new Map(); // Marker and finderMarker stored as an object by facilityId
    markerLayer: FeatureGroup;
    markerCluster: MarkerClusterGroup;
    excludeLayer: FeatureGroup;
    paradeRoute: Polyline;

    private zoomControl: Control.Zoom;
    private mapReadySubject = new Subject<Leaflet>();
    private mapIsLoadedSubject = new Subject<boolean>();
    private pinsDropped$ = new Subject<Leaflet>();
    private destroy$ = new Subject();
    private markersWithWaitTimes: Map<string, boolean> = new Map(); // Lets us filter down the number of markers we're checking

    /**
     * Create a map object using leaflet
     */
    constructor(
        container: string,
        defaults: any,
        private mapCardService: MapCardService,
        private operatingStatusService: OperatingStatusService,
        private finderAnalyticsService: FinderAnalyticsService,
        private configService: ConfigService,
        private debugLog: DebugLogService,
        private windowRef: WindowRef,
    ) {
        // Manually bring in canvas markers
        this.importCanvasPlugin();

        this.map = map(container, defaults);

        // Add zoom listener - this adds/removes a class so we can show/hide the marker icons
        this.map.on(LEAFLET_CONSTANTS.ZOOM_END, (e) => {
            const currentZoom = this.map.getZoom();
            this.debugLog.log(`leaflet zoom level:`, currentZoom);
            const markerPane = this.map.getPane('markerPane');
            const hideClass = 'icon-hide';
            if (currentZoom < CLUSTER_LEVEL) {
                markerPane.classList.add(hideClass);
            } else {
                markerPane.classList.remove(hideClass);
            }
        });

        // Listen for updates over webSockets
        this.listenForMarkerUpdates();
    }

    /**
     * Remove all listeners from the markers and the map
     */
    cleanup() {
        // @TODO: this is a temporary fix until this PR is merged https://github.com/Leaflet/Leaflet.markercluster/pull/1005
        if (this.markerCluster) {
            this.markerCluster.off('clusterkeypress', this.onKeypress);
        }

        // Get rid of the existing markers
        this.cleanUpMarkers();

        // Things get weird if there are two maps on one page.
        if (this.map) {
            // Clean up the map
            this.map.off();

            // Remove the map
            this.map.remove();
        }

        // Remove the status listeners
        this.destroy$.next(true);
        this.destroy$.complete();
    }

    cleanUpMarkers() {
        // Close any open popups
        this.closePopups();

        // Clear any marker that have a hover state
        this.clearMarkersHoverStatus();

        // Remove triggers for markers
        this.markers.forEach(marker => marker.off());

        // Get rid of the markers
        this.markers.clear();
        this.markersByFacilityId.clear();
        this.markersWithWaitTimes.clear();
    }

    clearMarkersHoverStatus() {
        // Clear the status for all the markers
        this.iconHover.forEach((value, key) => {
            const markerPin = this.markers.get(key);
            // Check for a marker before accessing methods
            if (!markerPin) {
                return;
            }
            markerPin.fire(MOUSE.MOUSE_OUT);
        });

        // Remove the hover markers from the map
        this.iconHover.clear();
    }

    /**
     * Create a wrapper for set view
     */
    setView(lat: number, lng: number, zoom: number) {
        this.map.setView(latLng(lat, lng), zoom);
    }

    /**
     * Let other classes know when the map is ready
     */
    mapIsReady(): Observable<Leaflet> {
        return this.mapReadySubject.asObservable();
    }

    /**
     * Redraw the map
     */
    redrawMap() {
        this.map.invalidateSize(true);
    }

    pinsAreDropped(): Observable<Leaflet> {
        return this.pinsDropped$.asObservable();
    }

    /**
     * Set the max bounds for the map
     */
    setMaxBounds(southWest, northEast) {
        const corner1 = latLng(southWest.lat, southWest.lng);
        const corner2 = latLng(northEast.lat, northEast.lng);

        this.map.setMaxBounds(latLngBounds(corner1, corner2));

        // Use this to debug if the map bounds are displaying correctly
        // L.rectangle([[southWest.lat, southWest.lng], [northEast.lat, northEast.lng]], { color: '#000', weight: 1}).addTo(this.map);
    }

    /**
     * Create tiles
     */
    setTileLayers(userTileLayer, options) {
        const layer = tileLayer(userTileLayer, options);

        // Listen for the load event
        // Let others know the map is ready
        layer.once(LEAFLET_CONSTANTS.LOAD, () => {
            this.redrawMap();
            this.mapReadySubject.next(this);
        });

        layer.addTo(this.map);

        return layer;
    }

    /**
     * Set zoom controls on the map
     */
    setZoomControls(options) {
        // If there is a zoom control on the map we need to remove it first
        if (this.zoomControl) {
            this.removeZoomControl();
        }

        // Set the options and add it to the map
        this.zoomControl = control.zoom(options).addTo(this.map);
    }

    /**
     * Remove the zoom control from the page
     */
    removeZoomControl() {
        this.zoomControl.remove();
    }

    /**
     * Remove the tap control from the page
     */
    /* istanbul ignore next */
    removeTapControl() {
        // if we are in a desktop using responsive
        // the tap object doesn't exist
        if (this.map['tap']) {
            this.map['tap'].disable();
        }
    }

    /**
     * Set the parade route on the map
     */
    setParadeRoute(paradeRoute: Array<any>) {
        // Create the line
        this.paradeRoute = polyline(paradeRoute, FINDER_MAP_BASE.path).addTo(this.map);
    }

    /**
     * Focus on the parade route
     */
    focusOnParadeRoute() {
        if (!this.paradeRoute) {
            return;
        }

        // Zoom to the line
        this.map.fitBounds(this.paradeRoute.getBounds());
    }

    /**
     * Focus on a group of pins
     */
    focusOnPins(markerOptions?: MarkerOptions) {
        this.redrawMap();

        // Stop if there are no markers
        if (this.markers.size === 0) {
            return;
        }

        // We'll either have a normal map or a cluster
        const activeLayer: FeatureGroup = this.markerCluster || this.markerLayer;

        const bounds = activeLayer.getBounds();
        /**
         * fitBounds throws an exception when the bounds are not valid
         * killing any javascript process where this was called
         * verify first before calling it
         */
        if (!bounds.isValid()) {
            return;
        }
        /**
         * Focus on the points
         * Use can use .pad() here to give the pins some breathing room
         * https://leafletjs.com/reference-1.6.0.html#latlngbounds-pad
         * There's also a padding option on fitbounds
         * https://leafletjs.com/reference-1.6.0.html#fitbounds-options-padding
         */
        this.map.fitBounds(bounds.pad(0.05), markerOptions || {});
    }

    /**
     * Create markers from an array of information and add them to the map
     */
    setMarkers(finderMarkers: Array<MapDataLocationMarker>, markerOptions: MarkerOptions) {
        const markerEvents = [];
        const markers = [];
        const allowClustering = markerOptions.allowClustering || false;
        // We'll either have a normal map or a cluster
        let activeLayer: FeatureGroup = this.markerCluster || this.markerLayer;

        // Clean up the markers that existed from a previous set
        this.cleanUpMarkers();

        // Clear any previous markers
        if (activeLayer) {
            activeLayer.clearLayers();
        }

        // Clear the exclude layer if it exists
        if (this.excludeLayer) {
            this.excludeLayer.clearLayers();
        }

        // If we need to cluster after displaying pins unclustered
        if (markerOptions.resetLayers) {
            activeLayer.removeFrom(this.map);
            activeLayer = null;
            // We don't want to reset the layers more than once unless intended
            markerOptions.resetLayers = false;
        }

        // Create marker cluster
        if (allowClustering && !activeLayer) {
            /**
             * The addition of canvas markers introduced a bug where the markers on the map were dimmed
             * when the cluster was opened. This is because the spider legs are drawn on the canvas and
             * change the opacity of the entire layer.
             * https://github.com/Leaflet/Leaflet.markercluster/blob/master/src/MarkerCluster.Spiderfier.js#L167
             * https://github.com/Leaflet/Leaflet/blob/master/src/layer/vector/Canvas.js#L331
             *
             * Now we pass through it's defaults and make the opacity 1 so it doesn't dim the whole
             * thing.
             * https://github.com/Leaflet/Leaflet.markercluster/blob/master/src/MarkerClusterGroup.js#L40
             */
            this.markerCluster = markerClusterGroup({
                ...MARKER_CLUSTER_OPTIONS,
                iconCreateFunction: (cluster) => divIcon({
                    html: this.mapCardService.getClusterMarker(cluster)
                })
            });
            // Add clustering to the map
            this.markerCluster.addTo(this.map);
            activeLayer = this.markerCluster;
            // @TODO: this is a temporary fix until this PR is merged https://github.com/Leaflet/Leaflet.markercluster/pull/1005
            activeLayer.on('clusterkeypress', this.onKeypress);
        } else if (!allowClustering && !activeLayer) {
            // Otherwise create normal layer
            this.markerLayer = featureGroup().addTo(this.map);
            activeLayer = this.markerLayer;
        }

        // Add all of the markers to the map
        finderMarkers.forEach(finderMarker => {
            // Create the marker
            const newMarker = this.createMarker(finderMarker, markerOptions);
            // Store the new marker for just a bit
            markers.push(newMarker);

            /**
             * https://youtu.be/0EefbG6N3vY
             * We're going to create an observable from the marker and listen for it to be added to the page.
             * The tricky part is that not every marker will emit an event. Grouped markers only emit one event
             * so we need to listen to the group to see when it's ready.
             *
             * We need to make sure all these events are unsubscribed after the first pin has been dropped. Listen
             * to the subject and unsubscribe all these markers
             */
            markerEvents.push(fromEvent(newMarker, LEAFLET_CONSTANTS.ADD).pipe(takeUntil(this.pinsDropped$)));
        });

        /**
         * Listen for mark events
         *
         * We will spread the markers into merge so we can listen for an event from any of the markers.
         * We can't use combineLatest
         * https://www.learnrxjs.io/learn-rxjs/operators/combination/combinelatest
         * It will only fire once EVERY observable has emitted at least once
         */
        merge(...markerEvents)
            .pipe(
                /**
                 * We need a fallback to force a re-render of the markers. If a marker is rendered
                 * outside the view of the map it won't emit an event. It doesn't matter what we emit
                 * here because the map object is passed along.
                 */
                timeout({ each: 600, with: () => of(1)}),
                // Wait until multiple markers have been placed
                debounceTime(500),
                // After the first emit the merge will unsubscribe
                first()
            )
            .subscribe(obj => {
                this.pinsDropped$.next(this);
            });

        /**
         * Place the markers on the page
         *
         * I know it's not great to loop through the markers twice but I need to get
         * observables for all of them before adding them to the page
         */
        markers.forEach(marker => {
            // Check for excluded marker
            if (marker?.options?.excludeMarker) {
                // Add to exclude layer
                marker.addTo(this.excludeLayer);
            } else {
                marker.addTo(activeLayer);
            }
        });
    }

    createIcon(finderMarker): DivIcon {
        // Track this marker for updates
        this.markersWithWaitTimes.set(finderMarker.id, true);

        return divIcon({
            className: 'marker-wait-time',
            /**
             * This adjusts the placement of the marker.
             * Use CSS to adjust the size. Look for [marker-size]
             */
            iconAnchor: [-5, -50],
            html: this.mapCardService.getWaitTime(finderMarker.card)
        });
    }

    /**
     * Create a marker that can be placed on a map
     */
    createMarker(finderMarker: MapDataLocationMarker, markerOptions: MarkerOptions): Marker {
        const { lat, lng, pin } = finderMarker;
        const pt = latLng(lat, lng);
        const iconOptions = this.getIconOptions(pin);
        const urlFriendlyId = finderMarker?.card?.urlFriendlyId;
        const SELECTED = 'selected';
        const newMarkerOptions: any = {
            popupOptions: {}
        };

        // Create and return the marker
        let newMarker: Marker;
        const isDPA = finderMarker.card?.disneyPremierAccess ?? false;

        if (finderMarker.card?.waitTime) {
            // Create the new icon
            const newIcon = this.createIcon(finderMarker);

            // This adjusts the placement of the popup
            // We need to adjust this if there's a DPA button (325px)
            const yOffset = isDPA ? 325 : 255;

            newMarker = L.marker(pt, { icon: newIcon });

            // Move the popup to below the marker
            newMarkerOptions.popupOptions.offset = [0, yOffset];
            newMarkerOptions.popupOptions.className = 'under' + (isDPA ? ' dpa-spacing' : '');
        } else {
            // Create the new icon
            const newIcon: Icon = icon(iconOptions);
            newMarker = L.canvasMarker(pt, {
                radius: 20,
                excludeMarker: finderMarker.excludeFromBounds,
                urlFriendlyId,
                img: {
                    url: newIcon.options.iconUrl,
                    size: newIcon.options.iconSize
                },
                className: newIcon.options.className
            });
            newMarkerOptions.popupOptions.className = (isDPA ? ' dpa-spacing' : '');
        }

        // Set mouse(over/out) events
        if (markerOptions.allowMarkerMouseOver) {
            newMarker.on(MOUSE.MOUSE_OVER, (event) => {
                const marker: Marker | any = event.target;

                // Check to see if it's an html marker
                if (marker.options?.icon?.options?.html) {
                    marker._icon.classList.add(SELECTED);

                    return;
                }

                const iconKey = this.getIconLatLngKey(marker.getLatLng(), marker?.options?.urlFriendlyId || '');
                const iconSettings = this.getIconOptions(pin, 'hover');

                // Store the current icon settings
                this.iconHover.set(iconKey, true);

                // Change the marker to the hover state
                marker.updateImg({
                    url: iconSettings.iconUrl,
                    size: iconSettings.iconSize
                });

                // Raise it to the top
                marker.bringToFront();
            });

            newMarker.on(MOUSE.MOUSE_OUT, (event) => {
                const marker: Marker | any = event.target;

                // Check to see if it's an html marker
                if (marker.options?.icon?.options?.html) {
                    marker._icon.classList.remove(SELECTED);

                    return;
                }

                const iconKey = this.getIconLatLngKey(marker.getLatLng(), marker?.options?.urlFriendlyId ?? '');
                const iconSettings = this.getIconOptions(pin);

                // Clear the icon
                this.iconHover.delete(iconKey);

                // Place the original marker back
                marker.updateImg({
                    url: iconSettings.iconUrl,
                    size: iconSettings.iconSize
                });

                // Restore the marker position
                marker.bringToBack();

            });
        }

        // Bind any available text
        if (markerOptions.showPopup && has(finderMarker, 'name')) {
            this.createPopup(newMarker, finderMarker, { markerOptions, ...newMarkerOptions });
        }

        // Store the marker so you can close the popup later
        this.markers.set(this.getIconLatLngKey(newMarker.getLatLng(), urlFriendlyId), newMarker);
        // Store the marker by facilityId so we can update it
        this.markersByFacilityId.set(finderMarker.id, {
            finderMarker,
            marker: newMarker
        });

        // Check to see if we should create an exclude layer
        if (finderMarker.excludeFromBounds && !this.excludeLayer) {
            // Create the new layer of it doesn't exist
            this.excludeLayer = featureGroup().addTo(this.map);
        }

        return newMarker;
    }

    listenForMarkerUpdates() {
        // Listen for changes to wait time and facility
        this.operatingStatusService.listenForUpdates$().pipe(
            takeUntil(this.destroy$),
            // Ignore the first placeholder response for the subject
            filter((statusUpdate) => statusUpdate),
            // Just check the markers we know have wait times
            filter((statusUpdate: OperatingStatus) =>
                // Make sure the facility actually has a wait time
                this.markersWithWaitTimes.has(statusUpdate.facilityId) &&
                // Make sure we have a marker for the facility
                this.markersByFacilityId.has(statusUpdate.facilityId)
            )
        ).subscribe((statusUpdate: OperatingStatus) => {
            // See if we have a marker that matches this status
            const { finderMarker, marker } = this.markersByFacilityId.get(statusUpdate.facilityId);

            // Update the wait time in finderMarker. It can come through as null
            if (Object.hasOwn(statusUpdate, 'waitTime')) {
                finderMarker.card.waitTime = statusUpdate.waitTime;
            }
            if (statusUpdate.operatingStatus) {
                finderMarker.card.operatingStatus = statusUpdate.operatingStatus;
            }

            // Update the map card
            marker.setPopupContent(this.mapCardService.getCard(finderMarker));
            // Update the map pin
            if (Object.hasOwn(statusUpdate, 'mapWaitTime')) {
                marker.setIcon(this.createIcon(finderMarker));
            }
        });
    }

    onKeypress(e) {
        if (e.originalEvent && e.originalEvent.keyCode === 13) {
            e.target._zoomOrSpiderfy(e);
        }
    }

    /**
     * Close any open popups
     */
    closePopups() {
        this.markers.forEach(finderMarker => {
            if (finderMarker.isPopupOpen()) {
                finderMarker.closePopup();
            }
        });
    }

    /**
     * Add a recenter button to the map
     */
    addRecenterControl(mapBtnOptions: MapButtonOptions) {
        const leafletOptions = {
            position: 'topright'
        };
        this.addMapControl(leafletOptions, mapBtnOptions);
    }

    /**
     * Remove the recenter button from the map
     */
    removeRecenterControl(mapBtnOptions: MapButtonOptions) {
        this.removeMapControl(mapBtnOptions);
    }

    /**
     * Add the expand map button to the map
     */
    addExpandControl(mapBtnOptions: MapButtonOptions) {
        const leafletOptions = {
            position: 'topright'
        };
        this.addMapControl(leafletOptions, mapBtnOptions);
    }

    /**
     * Remove the expand map button from the map
     */
    removeExpandControl(mapBtnOptions: MapButtonOptions) {
        this.removeMapControl(mapBtnOptions);
    }

    /**
     * Add a "return to list" button to the map
     */
    addListControl(mapBtnOptions: MapButtonOptions) {
        const leafletOptions = {
            position: 'topright'
        };
        this.addMapControl(leafletOptions, mapBtnOptions);
    }

    /**
     * Remove the list control from the map
     */
    removeListControl(mapBtnOptions: MapButtonOptions) {
        this.removeMapControl(mapBtnOptions);
    }

    /**
     * Enable dragging on the map
     */
    enableDragging() {
        this.map.dragging.enable();
    }

    /**
     * Disable dragging on the map
     */
    disableDragging() {
        this.map.dragging.disable();
    }

    /**
     * Pan to the selected marker and highlight it
     */
    jumpToMarker(finderMarker: MapDataLocationMarker, openPopup?: boolean) {
        const urlFriendlyId = finderMarker?.card?.urlFriendlyId;
        const pt = latLng(finderMarker.lat, finderMarker.lng);
        const markerPin = this.markers.get(this.getIconLatLngKey(pt, urlFriendlyId));

        const triggerPin = () => {
            // Highlight the marker
            markerPin.fire(MOUSE.MOUSE_OVER);

            // Check to see if we need to open the pin popup. This happens on guest service pages
            if (openPopup) {
                markerPin.openPopup();
            }

            /**
             * Redraw the map after we've centered on the pin. Redrawing here
             * allows the centering to finish first.
             */
            this.redrawMap();
        };

        // Make sure to close popups before we move anywhere
        this.closePopups();
        // Clear the other markers
        this.clearMarkersHoverStatus();

        /**
         * This check to make sure the marker still exists. It may be removed due to
         * the marker getting removed when new pins are set
         */
        if (!markerPin) {
            return;
        }

        /**
         * We can't use "this.map.once" because the MOVE_END event never
         * stops triggering. That's how we get a "max call stack exceeded" error
         */
        fromEvent(this.map, LEAFLET_CONSTANTS.MOVE_END)
            .pipe(
                /**
                 * We only want one of these events. They never stop.
                 */
                take(1)
            )
            .subscribe(() => {
                // Center the marker
                this.map.panTo(pt);

                // If the marker isn't visible that means it's in a cluster
                const isMarkerVisible: boolean = this.map.hasLayer(markerPin);

                /**
                 * If the marker isn't visible it's in a cluster. We need to get the cluster
                 * and clear the hover status of all pins inside then wait a tick to trigger
                 * the expand animation. It doesn't work if you do it all at once.
                 *
                 * Also the marker could be visible because it's a single pin or in an already
                 * expanded cluster so we don't have to wait.
                 */
                if (!isMarkerVisible) {
                    const activeLayer: any = this.markerCluster;
                    const cluster = activeLayer.getVisibleParent(markerPin);

                    // Clear hover status of all markers in cluster
                    cluster.getAllChildMarkers().forEach((marker: Marker) => marker.fire(MOUSE.MOUSE_OUT));

                    /**
                     * Sometimes a new cluster is created while zooming into the pin. We need
                     * to get the new cluster time to settle before calling a method on it. There
                     * is no 'new_cluster' event and trying to create one means putting a setTimeout
                     * somewhere else.
                     */
                    rxjsTimeout(() => {
                        // Open the cluster
                        cluster.spiderfy();
                        triggerPin();
                    }, 500);

                    return;
                }

                triggerPin();
            });

        // The higher the number the closer we are to the ground
        this.map.flyTo([finderMarker.lat, finderMarker.lng], CLUSTER_LEVEL);
    }

    enableScrollWheelZoom() {
        this.map.scrollWheelZoom.enable();
    }

    disableScrollWheelZoom() {
        this.map.scrollWheelZoom.disable();
    }

    /**
     * Get some default properties used to make a pin
     */
    private getIconOptions(pin = 'activities', type = 'default'): any {
        const props = ['iconUrl', 'iconSize', 'shadowUrl', 'shadowSize', 'className', 'shadowAnchor'];
        const pinObj = pick(MARKER_TYPES[pin], props);

        // Set the correct URL to the image
        pinObj.iconUrl = pinObj.iconUrl.replace('{type}', type);

        return pinObj;
    }

    /**
     * Get the lookup string for the icon object
     */
    private getIconLatLngKey(latLngObj: LatLng, urlFriendlyId: string) {
        return `${latLngObj.lat},${latLngObj.lng}-${urlFriendlyId}`;
    }

    /**
     * Create the popup and handle any accessibility issues
     */
    private createPopup(marker: Marker, finderMarker: MapDataLocationMarker, markerOptions: MarkerOptions) {
        let popup: string | HTMLElement = finderMarker.name;
        let popupOptions: PopupOptions = {
            offset: markerOptions?.popupOptions?.offset ?? [0, -11]
        };

        if (finderMarker.card) {
            // This will get the finder-map-card component and inject it into the leaflet popup
            popup = this.mapCardService.getCard(finderMarker);
            popupOptions = Object.assign(popupOptions, {
                closeButton: false,
                /**
                 * DO NOT USE keepInView. It caused the map to freeze after
                 * dragging it three times. The popup would disappear and stop
                 * working.
                 */
                // keepInView: true,
                autoPanPadding: markerOptions?.popupOptions?.autoPanPadding ?? [ 100, 100 ],
                // This is where the inline width on .leaflet-popup-content comes from
                // CSS sets popup to 400px, so match it - this could be even larger TBH
                // CSS will pretty much take over anyway since parent is 400px an will restrict child width
                minWidth: markerOptions?.popupOptions?.minWidth ?? 400,
                maxWidth: markerOptions?.popupOptions?.maxWidth ?? 400,
                // Pick up any class we might use for styling
                className: markerOptions?.popupOptions?.className ?? ''
            });
        }

        // Bind any popup details
        // https://leafletjs.com/reference.html#layer-bindpopup
        marker.bindPopup(popup, popupOptions);

        // Used for accessibility access
        const onKeyDown = (keyEvent) => {
            // If its not the escape key then ignore it
            if (keyEvent.code !== KEYBOARD.ESCAPE) {
                return;
            }

            // stopPropagation and preventDefault wouldn't stop the event from bubbling
            keyEvent.stopImmediatePropagation();

            /**
             * We have to bind the proper object to the popup so it can properly close it.
             * Not sure if this is a bug in the code, but it's the only way a popup will close.
             * "any" is used here so I can access the _source obj
             */
            const popupObj: any = marker.getPopup();
            popupObj.closePopup.apply(popupObj._source);
        };

        // Listen for the popup open event
        marker.on(LEAFLET_CONSTANTS.POPUP_OPEN, (popUpEvent: PopupEvent) => {
            // At this point, only the opened marker is available in the DOM, so we access the close button of that element
            // To add an Aria Label and Role
            const closeButtonPopupElement = this.windowRef.nativeWindow.document.querySelector('a.leaflet-popup-close-button');
            closeButtonPopupElement?.setAttribute('role', 'button');
            closeButtonPopupElement?.setAttribute('aria-label', finderMarker?.closeButtonLabel);

            if (finderMarker.card) {
                this.finderAnalyticsService.updateModelAndTrack({
                    linkId: this.configService.getValue('siteId') +
                    '_map_pin_' +
                    finderMarker.card.urlFriendlyId +
                    '_opened'});
            }
        });
    }

    /**
     * Used to add map buttons to the map
     */
    private addMapControl(leaftletOptions, mapBtnOptions: MapButtonOptions) {
        let container;

        const NewControl = Control.extend({
            options: leaftletOptions,
            onAdd: () => {
                const button = `
                    <a href="${mapBtnOptions.mapHostPageUrl || '#'}" id="mapSizeControl" class="icon" title="${mapBtnOptions.title}">
                        <span class="${mapBtnOptions.icon}">${mapBtnOptions.text || ''}</span>
                    </a>
                `;

                container = DomUtil.create('div', 'leaflet-bar leaftlet-control');
                container.innerHTML = button;

                // Listen for the button click
                DomEvent.on(container, MOUSE.CLICK, callbackFn);

                return container;
            },
            onRemove: () => {
                // Remove the listener
                DomEvent.off(container, MOUSE.CLICK, callbackFn);
            }
        });

        const callbackFn = mapBtnOptions.callback.bind(null, this);

        // Check to see if a expand control exists. If so remove it
        if (mapBtnOptions.controlReference) {
            this.removeMapControl(mapBtnOptions);
        }

        mapBtnOptions.controlReference = new NewControl();
        this.map.addControl(mapBtnOptions.controlReference);
    }

    /**
     * Remove the map control button
     */
    private removeMapControl(mapBtnOptions: MapButtonOptions) {
        if (mapBtnOptions.controlReference) {
            this.map.removeControl(mapBtnOptions.controlReference);
        }
    }

    /**
     * Import of this plugin
     * https://github.com/lipton-ice-tea/leaflet-canvas-markers
     *
     * Requested PR to include new features
     * https://github.com/lipton-ice-tea/leaflet-canvas-markers/pull/14
     */
    /* istanbul ignore next */
    private importCanvasPlugin() {
        L.Canvas.include({
            _updateImg(layer) {
                const { img } = layer.options;
                const p = layer._point.round();
                p.x += img.offset.x; p.y += img.offset.y;
                if (img.rotate) {
                    this._ctx.save();
                    this._ctx.translate(p.x, p.y);
                    this._ctx.rotate(img.rotate * Math.PI / 180);
                    this._ctx.drawImage(img.el, -img.size[0] / 2, -img.size[1] / 2, img.size[0], img.size[1]);
                    this._ctx.restore();
                } else {
                    this._ctx.drawImage(img.el, p.x - img.size[0] / 2, p.y - img.size[1] / 2, img.size[0], img.size[1]);
                }
            },
        });

        const angleCrds = (leafletMap, prevLatlng, latlng) => {
            if (!latlng || !prevLatlng) {
                return 0;
            }
            const pxStart = leafletMap.project(prevLatlng);
            const pxEnd = leafletMap.project(latlng);

            return Math.atan2(pxStart.y - pxEnd.y, pxStart.x - pxEnd.x) / Math.PI * 180 - 90;
        };

        const defaultImgOptions = {
            rotate: 0,
            size: [40, 40],
            offset: { x: 0, y: 0 },
        };

        const CanvasMarker = L.CircleMarker.extend({
            _updatePath() {
                if (!this.options.img || !this.options.img.url) {
                    return;
                }

                if (!this.options.img.el) {
                    this.options.img = {...defaultImgOptions, ...this.options.img};
                    this.options.img.rotate += angleCrds(this._map, this.options.prevLatlng, this._latlng);
                    this._createAndLoadImg();
                } else {
                    this._renderer._updateImg(this);
                }
            },
            _createAndLoadImg(updateImage) {
                const img = document.createElement('img');
                img.src = this.options.img.url;
                this.options.img.el = img;
                img.onload = () => {
                    if (updateImage) {
                        this._renderer._updateImg(this);
                    } else {
                        this.redraw();
                    }
                };
                img.onerror = () => {
                    this.options.img = null;
                };
            },
            updateImg(options) {
                // Check for the element. It should be set first
                if (!this.options.img.el) {
                    return;
                }

                // Check for options
                const { url, size, rotate, offset } = options;

                // Copy the new options onto the existing options
                if (url) {
                    this.options.img.url = url;
                }
                if (size) {
                    this.options.img.size = size;
                }
                if (rotate) {
                    this.options.img.rotate = rotate;
                }
                if (offset) {
                    this.options.img.offset = rotate;
                }

                this._createAndLoadImg(true);
            }
        });

        L.canvasMarker = (...opt) => {
            try {
                const i = opt.findIndex(o => typeof o === 'object' && o.img);
                if (i + 1) {
                    if (!opt[i].radius && opt[i].img && opt[i].img.size) {
                        opt[i].radius = Math.ceil(Math.max(...opt[i].img.size) / 2);
                    }
                    if (opt[i].pane) {
                        delete opt[i].pane;
                    }
                }
            } catch (e) {}

            return new CanvasMarker(...opt);
        };
    }
}
